import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1023 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Learn about IRS Form 1023 to apply for a tax-exempt status"
          description="Nonprofits use IRS Form 1023 to apply for the tax-exempt status or reinstate the status under section 501(c)(3). Learn more about its purpose and the process."
          keywords=""
          canonical="resources/tax-exempt/form-1023/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          {' '}
          <span className="highlight-container">
            <span className="highlight">Form 1023 </span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Nonprofits use this form to apply for tax-exempt status or to request
          <Hidden smDown>
            <br />
          </Hidden>
          reinstatement after automatic revocation.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1023`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-1023/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1023 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Is Form 1023? Form 1023
                        </strong>{' '}
                        is the IRS application used by organizations seeking
                        recognition of{' '}
                        <strong style={{ fontWeight: 600 }}>
                          federal tax-exempt status under Section 501(c)(3).
                        </strong>
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Who Should File?{' '}
                        </strong>{' '}
                        Organizations seeking{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/501c3-instructions/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          501(c)(3)
                        </Link>{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          recognition
                        </strong>
                        , generally use Form 1023 unless they qualify for the
                        streamlined{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 1023-EZ{' '}
                        </strong>
                        or fall under an IRS exception to the application
                        requirement.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Does It Establish?
                        </strong>{' '}
                        An approved Form 1023 application provides IRS
                        recognition that the organization qualifies for
                        exemption under{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Section 501(c)(3)
                        </strong>{' '}
                        and determines its applicable foundation classification.
                        .
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          How Is It Filed?
                        </strong>{' '}
                        Form 1023 must be{' '}
                        <strong style={{ fontWeight: 600 }}>
                          filed electronically through Pay.gov{' '}
                        </strong>{' '}
                        with the required user fee and supporting documents,
                        including the organization's organizing documents.
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Form 1023 vs. 1023-EZ:{' '}
                        </strong>
                        Eligible smaller organizations may use
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1023-EZ,
                        </strong>{' '}
                        while organizations that do not meet its eligibility
                        requirements must use the full Form 1023.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 1023?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Form 1023?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How many parts are there in IRS Form 1023?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#sch" sx={{ color: '#0202ff' }}>
                          Schedules of IRS Form 1023
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form" sx={{ color: '#0202ff' }}>
                          How to E-File Form 1023?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#process" sx={{ color: '#0202ff' }}>
                          What is the next process after submitting form1023?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Form 1023?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Organizations use Form 1023 to apply for an
                  &quot;tax-exempt&quot; status under section 501(c)(3) of the
                  IRS. Tax exempt status allows eligible nonprofit organizations
                  to be exempt from federal income tax and to receive
                  tax-deductible charitable contributions.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must File Form 1023?
                </Typography>
                <Typography variant="body1">
                  <span style={{ fontWeight: 600 }}> Form 1023 IRS</span> is
                  filed by nonprofit organizations operating under section
                  501(c)(3). They are{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Charitable and Religious organizations</li>
                  <li>Educational entities</li>
                  <li>Scientific organizations</li>
                  <li>Literary organizations </li>
                  <li>
                    Sports organizations (to foster national or international
                    amateur sports competition){' '}
                  </li>
                  <li>
                    Organizations prevent cruelty to children or animals.{' '}
                  </li>
                </Box>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesIcon />}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <strong style={{ fontWeight: 600 }}> Tip:</strong> If your
                  organization has{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    gross receipts ≤ $50,000
                  </span>
                  and{' '}
                  <span style={{ fontWeight: 600 }}>
                    total assets ≤ $250,000
                  </span>
                  , consider filing{' '}
                  <span style={{ fontWeight: 600 }}>Form 1023-EZ</span>, a
                  shorter version of Form 1023.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="how"
                >
                  How many parts are there in IRS Form 1023?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  IRS tax Form 1023 includes{' '}
                  <span style={{ fontWeight: 600 }}>11 parts</span> and you
                  should provide the necessary information for each section.
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 3, listStyleType: 'none', '& li': { mb: 1.3 } }}
                >
                  <li>
                    <span style={{ fontWeight: 600 }}>Part I-</span>{' '}
                    Organization details{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Part II-</span>{' '}
                    Organizational Structure
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Part III-</span> Required
                    Provisions in Your Organizing Document
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Part IV-</span> Narrative
                    Description of Your Activities
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Part V-</span>{' '}
                    Compensation and Other Financial Arrangements with Your
                    Officers, Directors, Trustees, Employees, and Independent
                    Contractors
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Part VI-</span> Your
                    Members and Other Individuals and Organizations That Receive
                    Benefits from You
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Part VII-</span> Your
                    History
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Part VIII-</span> Your
                    Specific Activities
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Part IX-</span> Financial
                    Data
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Part X-</span> Public
                    Charity Status
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Part XI-</span> User Fee
                    Information
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="sch"
                >
                  Schedules of IRS 1023 form
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form 1023 irs includes{' '}
                  <span style={{ fontWeight: 600 }}>8 schedules</span> which the
                  IRS requires to collect additional information about an
                  organization type or activities.{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.3 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Schedule A -</span>{' '}
                    Church{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Schedule B -</span>{' '}
                    school, college, or university
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Schedule C - </span>{' '}
                    Hospitals or Medical Research Organizations
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Schedule D - </span>{' '}
                    Section 509(a)(3) supporting organization
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Schedule E - </span>{' '}
                    Filing 27 months late or applying for reinstatement after{' '}
                    <Link
                      href="/resources/tax-exempt/automatic-revocation-of-tax-exempt-status/"
                      sx={{ color: '#0202ff' }}
                    >
                      automatic revocation
                    </Link>{' '}
                    of tax-exempt status.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Schedule F - </span>{' '}
                    Low-Income Housing Organizations
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Schedule G - </span>{' '}
                    Successors to Other Organizations
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Schedule H - </span>{' '}
                    Organizations Providing Scholarships, Fellowships,
                    Educational Loans, or other educational grants to
                    individuals and/or a private foundation.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="form"
                >
                  How to E-File Form 1023?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  E-file IRS 1023 Form with the IRS by following the given
                  steps.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Step 1:</span> Create an
                  account on{' '}
                  <Link
                    href="https://www.pay.gov/public/home"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    www.pay.gov
                  </Link>{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Step 2:</span> Select the
                  Internal Revenue service Form 1023
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Step 3:</span> Fill out all
                  applicable parts (Parts I–XI) and attach required schedules
                  (A–H)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Step 4:</span> Submit form
                  1023 to the IRS.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="process"
                >
                  What is the next process after submitting form1023?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  After Submitting Form 1023, the IRS will issue a{' '}
                  <span style={{ fontWeight: 600 }}> determination letter</span>{' '}
                  confirming the organization&apos;s tax-exempt status and
                  annual filing requirements (based on gross receipt and total
                  assets)
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.3 } }}>
                  <li>
                    <Link
                      href="/tax-exempt/e-file-irs-form-990/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990
                    </Link>{' '}
                    is filed by larger organizations with gross receipts greater
                    than or equal to $200,000 or total assets greater than or
                    equal to $500,000{' '}
                  </li>
                  <li>
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-ez/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990-EZ
                    </Link>{' '}
                    is for mid-size organizations with gross receipts under
                    $200,000 and total assets under $500,000{' '}
                  </li>
                  <li>
                    <Link
                      href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=form1023`}
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990-N{' '}
                    </Link>
                    is for small organizations with gross receipts of $50,000 or
                    less. <br />
                    <Alert
                      severity="warning"
                      icon={false}
                      sx={{
                        mb: 4,
                        mt: 2,
                        borderLeft: '3px solid rgb(102, 60, 0)',
                      }}
                    >
                      <strong style={{ fontWeight: 600 }}> Note:</strong> File
                      Form 990-N with TaxZerone at{' '}
                      <span style={{ fontWeight: 600 }}> no cost</span> for the
                      current tax year.
                    </Alert>
                  </li>
                  <li>
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-pf/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990-PF
                    </Link>{' '}
                    is filed by both exempt and taxable private foundations to
                    provide a detailed report of the foundation’s assets,
                    liabilities, income, and expenses.
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '16px !important',
                          fontWeight: 600,
                          mb: 1,
                          lineHeight: 1.5,
                        }}
                      >
                        File Form 990 series using TaxZerone for an easy filing
                        process
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1023`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        IRS website. For more information, check the
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1023.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          IRS instructions{' '}
                        </Link>
                        for Form 1023 and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1023ez#en_US_202501_publink1000102370"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          Instructions
                        </Link>{' '}
                        for Form 1023-EZ.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to Apply for tax-exempt status?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Submit your completed Form 1023 to the IRS and e-file your annual
              form 990 series using TaxZerone.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1023`}
              >
                E-file Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1023;
